import { apiFetch, get, patch, post } from "./api";

export type Company = {
  id: string;
  name: string;
  slug: string;
  /** FR-064: package codes the company bought, e.g. ["dw-advance"]. */
  purchasedPackages: string[];
  /** FR-064: individual apps bought outside a package. */
  purchasedSingularApps: string[];
  /** FR-064: DERIVED — packages + singular apps + the always-free apps. This is what FR-004's
   * eligibility matching reads; it is not edited directly. */
  apps: string[];
  instantAnswerReveal: boolean;
  logoKey: string | null;
  createdAt: number;
};

export type CompanySelectionInput = {
  purchasedPackages?: string[];
  purchasedSingularApps?: string[];
};
export type CreateCompanyInput = { name: string } & CompanySelectionInput;
export type UpdateCompanyInput = CompanySelectionInput & {
  instantAnswerReveal?: boolean;
  logoKey?: string | null;
};

export function listCompanies() {
  return get<Company[]>("/co/companies");
}

export function getCompany(id: string) {
  return get<Company>(`/co/companies/${encodeURIComponent(id)}`);
}

export function createCompany(input: CreateCompanyInput) {
  return post<Company>("/co/companies", input);
}

export function updateCompany(id: string, input: UpdateCompanyInput) {
  return patch<Company>(`/co/companies/${encodeURIComponent(id)}`, input);
}

/** FR-049: mirrors the backend's PositionType — org level, not dashboard permission. */
export type PositionType = "employee" | "manager" | "bod";

export type Member = {
  id: string;
  systemId: string;
  fullName: string;
  email: string;
  passwordVersion: number;
  department: string | null;
  positionType: PositionType;
  isManager: boolean;
  takesTest: boolean;
  createdAt: number;
};

export type UpdateMemberInput = {
  department?: string | null;
  positionType?: PositionType;
  isManager?: boolean;
  takesTest?: boolean;
  password?: string;
};

export function listMembers(companyId: string) {
  return get<Member[]>(`/co/companies/${encodeURIComponent(companyId)}/members`);
}

export function updateMember(companyId: string, memberId: string, input: UpdateMemberInput) {
  return patch<Member>(
    `/co/companies/${encodeURIComponent(companyId)}/members/${encodeURIComponent(memberId)}`,
    input,
  );
}

export function renameDepartment(companyId: string, oldName: string, newName: string) {
  return patch<{ department: string }>(
    `/co/companies/${encodeURIComponent(companyId)}/departments/${encodeURIComponent(oldName)}`,
    { newName },
  );
}

export type RosterUploadResult = { created: number; rejectedRows: { row: number; reason: string }[] };

export function uploadRoster(companyId: string, file: File) {
  const form = new FormData();
  form.set("file", file);
  return apiFetch<RosterUploadResult>(`/co/companies/${encodeURIComponent(companyId)}/roster`, {
    method: "POST",
    body: form,
  });
}

export type EligibleQuestion = {
  id: string;
  text: string;
  language: "vi" | "en" | "km";
  type: string;
};

export function getEligibleQuestions(companyId: string) {
  return get<EligibleQuestion[]>(`/co/companies/${encodeURIComponent(companyId)}/eligible-questions`);
}

export type Assignment = {
  id: string;
  systemId: string;
  timeLimitMinutes: number | null;
  passFailThresholdPercent: number | null;
  createdByCoMemberId: string;
  createdAt: number;
};

export type CreateAssignmentInput = {
  questionIds: string[];
  timeLimitMinutes?: number | null;
  passFailThresholdPercent?: number | null;
};

export function listAssignments(companyId: string) {
  return get<Assignment[]>(`/co/companies/${encodeURIComponent(companyId)}/assignments`);
}

export function createAssignment(companyId: string, input: CreateAssignmentInput) {
  return post<Assignment>(`/co/companies/${encodeURIComponent(companyId)}/assignments`, input);
}

export type SubmissionAnswer = {
  given: string[] | string | null;
  isCorrect: boolean | null;
  gradedBy: string | null;
  gradedAt: number | null;
};

export type Submission = {
  id: string;
  systemId: string;
  assignmentId: string;
  memberId: string;
  status: "not_started" | "in_progress" | "submitted" | "pending_grading" | "completed";
  answers: Record<string, SubmissionAnswer>;
  scorePercent: number | null;
  passed: boolean | null;
  explanationsReleasedAt: number | null;
  startedAt: number | null;
  submittedAt: number | null;
  finalizedAt: number | null;
};

export function listSubmissions(companyId: string, status?: Submission["status"]) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return get<Submission[]>(`/co/companies/${encodeURIComponent(companyId)}/submissions${qs}`);
}

export function gradeSubmission(
  companyId: string,
  submissionId: string,
  answers: { questionId: string; isCorrect: boolean }[],
) {
  return post<Submission>(
    `/co/companies/${encodeURIComponent(companyId)}/submissions/${encodeURIComponent(submissionId)}/grade`,
    { answers },
  );
}

export function releaseExplanations(companyId: string, submissionId: string) {
  return post<{ explanationsReleasedAt: number }>(
    `/co/companies/${encodeURIComponent(companyId)}/submissions/${encodeURIComponent(submissionId)}/release-explanations`,
  );
}
